import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Callout } from "@promptgatewaysadmin/ui";
import { useAuth } from "../../auth/AuthContext.js";
import { usePortalClient } from "../../api/PortalClientProvider.js";
import { createOrg } from "../../api/generated/identity.generated.js";
import { readFunnel } from "../../auth/persistence.js";
import { qk } from "../../queries/keys.js";
import { POLL } from "../../queries/keys.js";
import { errorCopy } from "../../copy/errors.js";

/**
 * /verify
 *
 * Two paths in parallel, because most users take the passive one:
 *
 *   passive   reload the GCIP user every 5s for the first 2 minutes, then stop. If
 *             emailVerified flips, advance automatically and announce politely.
 *   explicit  "I've verified — continue" re-checks the SERVER. If still false it does NOT
 *             navigate — a button that pretends to work is the worst outcome on this screen.
 *
 * On success, a team/business signup CREATES THE ORG here. That resolves the intent:"none"
 * from signup and seeds the 14-day trial via identity.org.created.
 */
export function Verify() {
  const auth = useAuth();
  const client = usePortalClient();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [announcement, setAnnouncement] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const advancing = useRef(false);

  const funnel = readFunnel();
  const needsOrg = funnel.plan === "team";

  const advance = useCallback(async () => {
    if (advancing.current) return;
    advancing.current = true;
    try {
      if (needsOrg) {
        // Exactly once. A second call would create a second org and a second trial.
        await client.get(createOrg, "auth.verify", { body: { name: "My team" } });
      }
      await qc.invalidateQueries({ queryKey: qk.me() });
      setAnnouncement("Email verified. Taking you to setup.");
      navigate("/onboarding", { replace: true });
    } catch {
      advancing.current = false;
      setInlineError("error.unavailable");
    }
  }, [client, navigate, needsOrg, qc]);

  // The passive poll, with an explicit stop condition. Never a bare interval.
  useEffect(() => {
    let elapsed = 0;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      elapsed += POLL.verifyEmail.intervalMs;
      const verified = await auth.reloadUser();
      if (verified) {
        stopped = true;
        void advance();
        return;
      }
      if (elapsed < POLL.verifyEmail.ceilingMs) {
        timer = setTimeout(() => void tick(), POLL.verifyEmail.intervalMs);
      }
    };
    let timer = setTimeout(() => void tick(), POLL.verifyEmail.intervalMs);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [auth, advance]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  return (
    <>
      <h1>Check your inbox</h1>
      <p>
        We sent a verification link to <strong>{auth.email}</strong>. Click it and we&apos;ll
        pick up right here.
      </p>

      {inlineError && (
        <Callout tone="danger" role="alert">
          {errorCopy(inlineError).body}
        </Callout>
      )}

      <Button
        block
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setInlineError(null);
          const verified = await auth.reloadUser();
          setBusy(false);
          if (verified) void advance();
          // Do NOT navigate on a false. Say what we saw.
          else setInlineError("error.auth.notVerifiedYet");
        }}
      >
        I&apos;ve verified — continue
      </Button>

      <Button
        variant="secondary"
        block
        aria-disabled={cooldown > 0 || undefined}
        aria-describedby={cooldown > 0 ? "resend-cooldown" : undefined}
        onClick={async () => {
          if (cooldown > 0) return;
          try {
            // The client SDK sends it. Identity's resend endpoint 202s and sends nothing.
            await auth.sendVerificationEmail();
            setCooldown(60);
            setAnnouncement("Verification email sent.");
          } catch {
            // auth/too-many-requests is a cooldown, not an error wall.
            setCooldown(60);
          }
        }}
      >
        {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
      </Button>
      {cooldown > 0 && (
        <p id="resend-cooldown" className="pg-disabled-reason">
          You can send another email in {cooldown} seconds.
        </p>
      )}

      {/* The single most common support ticket for this screen. */}
      <p className="pg-auth__alt">
        Wrong address?{" "}
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            await auth.signOut();
            navigate("/signup", { replace: true });
          }}
        >
          Change it
        </Button>
      </p>

      <span className="pg-visually-hidden" role="status" aria-live="polite">
        {announcement}
      </span>
    </>
  );
}
